import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api, type Collection, type CollectionAccessGrant, type DocListItem } from '../lib/api';
import SmartdoxxLogo from '../components/SmartdoxxLogo';

function SortableDocRow({ doc, index }: { doc: DocListItem; index: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: doc.slug });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-4 py-3 bg-background border border-border rounded-lg ${isDragging ? 'opacity-50' : ''}`}
    >
      <button
        type="button"
        className="cursor-grab text-muted-foreground hover:text-foreground touch-none"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-xs font-mono text-muted-foreground min-w-[1.5rem]">{index + 1}.</span>
      <span className="flex-1 text-sm text-foreground">{doc.title}</span>
    </li>
  );
}

export default function AdminAccess() {
  const { org, collection } = useParams<{ org: string; collection: string }>();
  const [loading, setLoading] = useState(true);
  const [collectionData, setCollectionData] = useState<Collection | null>(null);
  const [grants, setGrants] = useState<CollectionAccessGrant[]>([]);
  const [docs, setDocs] = useState<DocListItem[]>([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [togglingPublic, setTogglingPublic] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    async function loadData() {
      try {
        const [col, accessGrants, docList] = await Promise.all([
          api.orgs.collection(org!, collection!),
          api.access.list(org!, collection!),
          api.docs.list(org!, collection!),
        ]);
        setCollectionData(col);
        setGrants(accessGrants);
        if (docList.documents) {
          setDocs([...docList.documents].sort((a, b) => a.display_order - b.display_order));
        }
      } catch (err: any) {
        console.error('Error loading admin data:', err);
        setError(err?.message ?? 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [user, org, collection, navigate]);

  const handleTogglePublic = async () => {
    if (!collectionData || !org || !collection) return;
    setTogglingPublic(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await api.orgs.setVisibility(org, collection, !collectionData.is_public);
      setCollectionData(prev => prev ? { ...prev, is_public: result.is_public, public_url: result.public_url } : prev);
      setSuccess(`${result.is_public ? 'Enabled' : 'Disabled'} public access for ${collectionData.name}`);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to toggle public access');
    } finally {
      setTogglingPublic(false);
    }
  };

  const copyToClipboard = async () => {
    if (!collectionData?.public_url) return;
    try {
      await navigator.clipboard.writeText(collectionData.public_url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      // fallback — do nothing
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email) {
      setError('Please enter a user email');
      return;
    }

    setIsSubmitting(true);
    try {
      const newGrant = await api.access.grant(org!, collection!, email);
      setGrants(prev => [...prev, newGrant]);
      setSuccess(`Successfully granted access to ${email}`);
      setEmail('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to grant access');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !org || !collection) return;

    const oldIndex = docs.findIndex((d) => d.slug === active.id);
    const newIndex = docs.findIndex((d) => d.slug === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = docs;
    const reordered = arrayMove(docs, oldIndex, newIndex);
    setDocs(reordered);
    setError(null);
    setSuccess(null);

    try {
      await api.docs.reorder(org, collection, reordered.map((d) => d.slug));
    } catch (err: any) {
      setDocs(previous);
      setError(err?.message ?? 'Failed to save document order');
    }
  };

  const handleRevoke = async (grantId: string, grantEmail: string) => {
    if (!confirm(`Revoke access for ${grantEmail}?`)) return;
    setError(null);
    setSuccess(null);

    try {
      await api.access.revoke(org!, collection!, grantId);
      setGrants(prev => prev.filter(g => g.id !== grantId));
      setSuccess(`Revoked access for ${grantEmail}`);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to revoke access');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
      <header className="bg-card shadow-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <Link to="/dashboard">
            <SmartdoxxLogo size="md" showText={true} />
          </Link>
          <Link
            to="/dashboard"
            className="px-3 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 transition"
          >
            Back to Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 lg:px-8 py-8 w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-1">Access Management</h1>
          <p className="text-muted-foreground">{collectionData?.name ?? collection}</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-800 dark:text-green-300">{success}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* Public Access */}
          <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground">Public Access</h2>
              <p className="text-sm text-muted-foreground">Make this collection accessible without login</p>
            </div>

            {collectionData?.is_public ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-sm font-medium">This collection is publicly accessible</span>
                </div>

                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <code className="text-sm text-foreground flex-1 min-w-0 truncate">
                    {collectionData.public_url}
                  </code>
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition whitespace-nowrap"
                  >
                    {copiedUrl ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>

                <button
                  onClick={handleTogglePublic}
                  disabled={togglingPublic}
                  className="px-4 py-2 bg-muted text-foreground rounded-md text-sm font-medium hover:bg-muted/80 transition disabled:opacity-50"
                >
                  {togglingPublic ? 'Processing...' : 'Make Private'}
                </button>
              </div>
            ) : (
              <button
                onClick={handleTogglePublic}
                disabled={togglingPublic}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
              >
                {togglingPublic ? 'Processing...' : 'Make Public'}
              </button>
            )}
          </div>

          {/* Individual User Access */}
          <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground">Individual User Access</h2>
              <p className="text-sm text-muted-foreground">Grant and manage access to {collectionData?.name ?? collection}</p>
            </div>

            <form onSubmit={handleSubmit} className="flex gap-4 mb-6">
              <div className="flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition bg-background text-foreground"
                  placeholder="user@example.com"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg shadow-md hover:opacity-90 transition duration-200 flex items-center justify-center disabled:opacity-50"
              >
                {isSubmitting ? 'Processing...' : 'Grant Access'}
              </button>
            </form>

            {grants.length > 0 ? (
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-foreground uppercase">User</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-foreground uppercase">Access Level</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-foreground uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {grants.map((g) => (
                      <tr key={g.id} className="hover:bg-muted/30 transition">
                        <td className="px-6 py-3">
                          <span className="text-sm font-medium text-foreground">{g.user_email}</span>
                        </td>
                        <td className="px-6 py-3 text-center">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            g.level === 'ADMIN'
                              ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300'
                              : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                          }`}>
                            {g.level === 'ADMIN' ? 'Admin' : 'Viewer'}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-center">
                          <button
                            onClick={() => handleRevoke(g.id, g.user_email)}
                            className="text-destructive hover:text-destructive/80 text-sm font-medium transition"
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No users have been granted access yet.</p>
            )}
          </div>

          {/* Document Order */}
          {docs.length > 1 && (
            <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground">Document Order</h2>
                <p className="text-sm text-muted-foreground">Drag documents to change their order in the navigation</p>
              </div>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={docs.map((d) => d.slug)} strategy={verticalListSortingStrategy}>
                  <ul className="space-y-2">
                    {docs.map((doc, i) => (
                      <SortableDocRow key={doc.slug} doc={doc} index={i} />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
